"use client"

import React, { createContext, useContext, useState, useEffect } from "react"
import type { Product } from "./types"

export interface CartItem {
    product: Product
    quantity: number
    price: number // Prix unitaire final (avec promo si applicable)
    originalPrice?: number // Prix original avant promo
    promotionId?: string | null
}

interface CartContextType {
    cart: CartItem[]
    addToCart: (product: Product, quantity: number, price?: number, promotionId?: string | null) => void
    removeFromCart: (productId: string) => void
    updateQuantity: (productId: string, quantity: number) => void
    clearCart: () => void
    getTotalItems: () => number
    getTotalPrice: () => number
    getCartItem: (productId: string) => CartItem | undefined
}

const CartContext = createContext<CartContextType | undefined>(undefined)

const CART_STORAGE_KEY = "stockwise_cart"

export function CartProvider({ children }: { children: React.ReactNode }) {
    const [cart, setCart] = useState<CartItem[]>([])
    const [isHydrated, setIsHydrated] = useState(false)

    // Charger le panier depuis le localStorage au montage
    useEffect(() => {
        try {
            const stored = localStorage.getItem(CART_STORAGE_KEY)
            if (stored) {
                const parsed = JSON.parse(stored)
                setCart(parsed)
            }
        } catch (error) {
            console.error("Erreur lors du chargement du panier:", error)
        } finally {
            setIsHydrated(true)
        }
    }, [])

    // Sauvegarder le panier dans le localStorage à chaque modification
    useEffect(() => {
        if (isHydrated) {
            try {
                localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cart))
            } catch (error) {
                console.error("Erreur lors de la sauvegarde du panier:", error)
            }
        }
    }, [cart, isHydrated])

    const addToCart = (
        product: Product,
        quantity: number,
        price?: number,
        promotionId?: string | null
    ) => {
        setCart((prev) => {
            const existingIndex = prev.findIndex((item) => item.product._id === product._id)

            if (existingIndex !== -1) {
                // Le produit existe déjà, mettre à jour la quantité
                const updated = [...prev]
                updated[existingIndex] = {
                    ...updated[existingIndex],
                    quantity: updated[existingIndex].quantity + quantity,
                    price: price ?? updated[existingIndex].price,
                    promotionId: promotionId !== undefined ? promotionId : updated[existingIndex].promotionId
                }
                return updated
            }

            // Nouveau produit
            return [
                ...prev,
                {
                    product,
                    quantity,
                    price: price ?? product.price,
                    originalPrice: product.price,
                    promotionId: promotionId ?? null
                }
            ]
        })
    }

    const removeFromCart = (productId: string) => {
        setCart((prev) => prev.filter((item) => item.product._id !== productId))
    }

    const updateQuantity = (productId: string, quantity: number) => {
        if (quantity <= 0) {
            removeFromCart(productId)
            return
        }

        setCart((prev) =>
            prev.map((item) =>
                item.product._id === productId
                    ? { ...item, quantity }
                    : item
            )
        )
    }

    const clearCart = () => {
        setCart([])
    }

    const getTotalItems = () => {
        return cart.reduce((total, item) => total + item.quantity, 0)
    }

    const getTotalPrice = () => {
        return cart.reduce((total, item) => total + item.price * item.quantity, 0)
    }

    const getCartItem = (productId: string) => {
        return cart.find((item) => item.product._id === productId)
    }

    const value = {
        cart,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        getTotalItems,
        getTotalPrice,
        getCartItem,
    }

    return <CartContext.Provider value={value}>{children}</CartContext.Provider>
}

export function useCart() {
    const context = useContext(CartContext)
    if (context === undefined) {
        throw new Error("useCart must be used within a CartProvider")
    }
    return context
}
